import { formatDate } from "@containers/Hermes/utils/formatCell";
import { Children, cloneElement, createContext, isValidElement, useContext } from "react";
import type { ReactElement, ReactNode } from "react";
import { countryFlagUrl } from "@containers/Hermes/utils/countryFlag";
import HermesCheckbox from "./HermesCheckbox";

/**
 * Row selection for one answer table (Zeplin: Hermes_Response_Actions_1 — a
 * checkbox column with select-all). Message provides it around a table that has
 * company actions beneath it; the markdown `thead` / `tbody` / `tr` below read it
 * to add the checkbox cells. Outside a provider they render plainly.
 */
export type TableSelection = {
  id: string;
  rowCount: number;
  selected: Set<number>;
  toggleRow: (index: number) => void;
  toggleAll: () => void;
  // Rendered directly above the table: the action bar for the ticked rows.
  topBar?: ReactNode;
  // Rendered inside the table's card, beneath its rows: the actions on the rows shown, and the offer to
  // choose from all of them (Zeplin: the table and its actions are one card).
  bottomBar?: ReactNode;
  // An action card is open on this selection: the ticks are frozen until it is closed.
  disabled?: boolean;
};

export const TableSelectionContext = createContext<TableSelection | null>(null);

// The columns (by position) of the table being rendered whose values are country names, which get a flag.
export const CountryColumnsContext = createContext<ReadonlySet<number>>(new Set());

const DATA_CELL = "tw-px-3 tw-py-3 tw-border-0 tw-border-b tw-border-solid tw-border-gray-200 tw-text-sm tw-leading-5 tw-text-gray-900 tw-tracking-[-0.07px]";

const plainText = (children: ReactNode): string => (typeof children === "string" ? children : Array.isArray(children) && children.every((child) => typeof child === "string") ? children.join("") : "");

/** A data cell of an answer's table; in a country column its name is shown with its flag. `columnIndex` is added by the row. */
export function FlagCell({ children, columnIndex }: { children?: ReactNode; columnIndex?: number }) {
  const countryColumns = useContext(CountryColumnsContext);
  const content = typeof children === "string" ? formatDate(children) : children;
  const flag = columnIndex !== undefined && countryColumns.has(columnIndex) ? countryFlagUrl(plainText(children).trim()) : undefined;
  return (
    <td className={DATA_CELL}>
      {flag ? (
        <span className="tw-inline-flex tw-items-center tw-gap-2">
          <img src={flag} alt="" width={20} height={14} className="tw-shrink-0 tw-rounded-[2px] tw-border tw-border-solid tw-border-gray-200 tw-object-cover" />
          {content}
        </span>
      ) : (
        content
      )}
    </td>
  );
}

/** Tells each data cell of a row which column it is. */
function withColumnIndex(children: ReactNode): ReactNode {
  let column = 0;
  return Children.map(children, (child) => {
    if (!isValidElement(child)) return child;
    const index = column++;
    return child.type === FlagCell ? cloneElement(child as ReactElement<{ columnIndex?: number }>, { columnIndex: index }) : child;
  });
}

type RowProps = { children?: ReactNode; rowIndex?: number; headerRow?: boolean };

const CELL = "tw-w-10 tw-px-3 tw-py-3 tw-border-0 tw-border-b tw-border-solid tw-border-gray-200";

export function SelectableRow({ children, rowIndex, headerRow }: RowProps) {
  const selection = useContext(TableSelectionContext);
  if (!selection || (!headerRow && rowIndex === undefined)) return <tr>{withColumnIndex(children)}</tr>;

  if (headerRow) {
    const all = selection.rowCount > 0 && selection.selected.size === selection.rowCount;
    return (
      <tr>
        <th className={CELL}>
          <HermesCheckbox id={`${selection.id}-all`} checked={all} disabled={selection.disabled} onChange={selection.toggleAll} />
        </th>
        {withColumnIndex(children)}
      </tr>
    );
  }
  return (
    <tr>
      <td className={CELL}>
        <HermesCheckbox id={`${selection.id}-${rowIndex}`} checked={selection.selected.has(rowIndex as number)} disabled={selection.disabled} onChange={() => selection.toggleRow(rowIndex as number)} />
      </td>
      {withColumnIndex(children)}
    </tr>
  );
}

/** Passes each row its index (and marks the header row); whitespace text between rows is skipped. */
function withRowProps(children: ReactNode, header: boolean): ReactNode {
  let index = 0;
  return Children.map(children, (child) => {
    if (!isValidElement(child)) return child;
    const row = child as ReactElement<RowProps>;
    return cloneElement(row, header ? { headerRow: true } : { rowIndex: index++ });
  });
}

export function SelectableThead({ children }: { children?: ReactNode }) {
  return <thead className="tw-sticky tw-top-0 tw-z-[1] tw-bg-gray-50">{withRowProps(children, true)}</thead>;
}

export function SelectableTbody({ children }: { children?: ReactNode }) {
  return <tbody>{withRowProps(children, false)}</tbody>;
}

/**
 * The table card: the table scrolls vertically past a fixed height with its header pinned, and the bar of
 * actions on the rows shown sits in the same card beneath it. With row selection, the ticked rows' action
 * bar sits directly above.
 */
export function SelectableTable({ children }: { children?: ReactNode }) {
  const selection = useContext(TableSelectionContext);
  return (
    <>
      {selection?.topBar}
      <div className="tw-my-4 tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-gray-200 tw-bg-white tw-shadow-[0_1px_3px_rgba(16,24,40,0.1),0_1px_2px_rgba(16,24,40,0.06)]">
        <div className="tw-overflow-auto tw-max-h-[420px]">
          <table className="tw-w-full tw-text-sm tw-border-separate tw-border-spacing-0 [&_tr:last-child_td]:tw-border-b-0 [&_tbody_tr:hover]:tw-bg-gray-25">{children}</table>
        </div>
        {selection?.bottomBar ? <div className="tw-border-0 tw-border-t tw-border-solid tw-border-gray-200 tw-bg-gray-50 tw-p-3">{selection.bottomBar}</div> : null}
      </div>
    </>
  );
}
